import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import { notify } from '../helper/notify';
import { useAppDispatch } from '../hooks/useRedux';
import { deleteDraftInvoice } from '../features/draft-invoice/draft-invoice.slice';
import { getDraftInvoiceDetails } from '../features/invoice/invoice.slice';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import {
    TableRow,
    TableCell,
    TableHeader,
    TableColumn,
    MobileCardRow,
    MobileCardWrapper,
    TableBody,
} from '../components/dashboard/table-components';
import './../scss/components/invoice-table-detailed.style.scss';
import { useDraftInvoice } from '../hooks/useDraftInvoice';
import { SearchIcon, ArrowLeftIcon, ArrowRightIcon, SendIcon, EditIcon, DeleteIcon, CheckIcon, EditIconBtn, DownloadIcon } from './../assets/dashboard-icon.svg';
import { formatAmount, getStageBadgeClass, getStatusBadgeClass, handleDownloadDraftInvoice, mapInvoiceStatus } from '../helper/common.helper';
import { calculatePaginationPages } from '../helper/pagination.helper';
import { Skeleton } from '@mui/material';

interface InvoiceTableDetailedProps {
    className?: string;
}

export const DraftInvoiceSection: React.FC<InvoiceTableDetailedProps> = ({
    className = ''
}) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
const [deleteLoading, setDeleteLoading] = useState(false);

    const { t } = useTranslation();
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const lang = new URLSearchParams(window.location.search).get('lang') || 'en';
    const {
        fetchDraftInvoices,
        invoicesData,
        loading,
        error,
        pagination,
        searchQuery,
        setCurrentPage,
        goToNextPage,
        goToPreviousPage,
        handleSearch
    } = useDraftInvoice();

    const dispatch = useAppDispatch();


    const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState<boolean>(false);
    const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<number | null>(null);
    const [editingInvoiceNumber, setEditingInvoiceNumber] = useState<string | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const currentYear = new Date().getFullYear();

    const getClientInitial = (name?: string) => {
        const parts = (name || '').trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return '?';
        return parts.slice(0, 2).map((p) => p.charAt(0)).join('').toUpperCase();
    };

    const getAvatarVariant = (name?: string) => {
        const key = (name || '').trim();
        let sum = 0;
        for (let i = 0; i < key.length; i++) sum += key.charCodeAt(i);
        return (sum % 5) + 1;
    };

    const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        handleSearch(query);

        if (!query.trim()) {
            setIsSearchDropdownOpen(false);
            return;
        }

        try {
            setIsSearchDropdownOpen(true);
        } catch (error: any) {
            console.error('Search error:', error);
            setIsSearchDropdownOpen(false);
        }
    };
    useEffect(() => {
        fetchDraftInvoices();
    }, [fetchDraftInvoices]);



    const handleEditButton = async (invoiceNumber: string) => {
        setEditingInvoiceNumber(invoiceNumber);
        try {
            navigate(`/dashboard/edit-invoice/${invoiceNumber}`);
        } catch (error: any) {
            console.error('Navigation error:', error);
            const errMsg = t('NAVIGATION_FAILED') || 'Failed to navigate to edit invoice';
            notify.error(typeof errMsg === 'string' ? errMsg : 'Failed to navigate to edit invoice');
        } finally {
            setEditingInvoiceNumber(null);
        }
    };
    const handleDraftInvoice = async (id: string) => {
        try {
            // Navigate with correct URL format first
            navigate(`/v2/create-invoice?lang=${lang}&preId=${id}`);

            // Then call API to get draft invoice details
            await dispatch(getDraftInvoiceDetails({ invoiceId: id, lang })).unwrap();
        } catch (error: any) {
            console.error('Failed to fetch draft invoice details:', error);
            const apiMsg = error?.response?.data?.message || (typeof error === 'string' ? error : error?.message);
            notify.error(apiMsg || (t('FAILED_TO_LOAD_DRAFT') as string) || 'Failed to load draft invoice');
        }
    }
const openDeleteModal = (id: number) => {
    setSelectedInvoiceId(id);
    setShowDeleteModal(true);
};

const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedInvoiceId(null);
};

const confirmDelete = async () => {
    if (!selectedInvoiceId) return;

    try {
        setDeleteLoading(true);
        await handleDeleteDraft(selectedInvoiceId);
    } finally {
        setDeleteLoading(false);
        closeDeleteModal();
    }
};

    const handleDeleteDraft = async (id: number) => {
        try {
            await dispatch(deleteDraftInvoice(id)).unwrap();
            notify.success(String(t('DELETE_SUCCESS')));
            // refresh list
            fetchDraftInvoices();
        } catch (error: any) {
            const msg = typeof error === 'string' ? error : (error?.message || t('DELETE_FAILED') || 'Failed to delete draft');
            notify.error(String(msg));
        }
    }

    return (
        <div className={`invoice-table-container ${className}`}>
            <div className="invoice-table-header">
                <h3 className="invoice-table-title">
                    {t('DRAFT_INVOICES')}
                    <span className="mi-count-badge">{pagination?.totalCount ?? (Array.isArray(invoicesData) ? invoicesData.length : 0)}</span>
                </h3>

                <div className="invoice-table__filter-bar">
                    <div className="invoice-table__search-input" ref={dropdownRef}>
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder={t('SEARCH_INVOICE_OR_CLIENT', { defaultValue: 'Search invoice or client' })}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            className="invoice-table__search-field"
                        />
                    </div>
                </div>
            </div>

            <div className="invoice-table-content">

                {/* Loading shimmer — replaces only the table BODY while
                    fetching. The .invoice-table-header (title + count)
                    and .invoice-table__filter-bar (search input) stay
                    mounted across re-fetches. Previously the WHOLE
                    component early-returned behind <InvoiceTableSkeleton>
                    on every keystroke in the search box, which read as a
                    full-page refresh and stole focus from the input
                    mid-type. Reusing the table's real BEM classes
                    (.invoice-table__body / __desktop-row / __desktop-cell)
                    so the shimmer occupies the same column widths as the
                    real rows. */}
                {loading && (
                    <div className="invoice-table-scroll-wrapper">
                        <div className="invoice-table">
                            <div className="invoice-table__body">
                                {Array.from({ length: 6 }).map((_, r) => (
                                    <div className="invoice-table__desktop-row" key={r}>
                                        {Array.from({ length: 5 }).map((_, c) => (
                                            <div className="invoice-table__desktop-cell" key={c}>
                                                <Skeleton variant="text" width="72%" height={16} animation="wave" />
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Table */}
                {!loading && !error && invoicesData && (Array.isArray(invoicesData) && invoicesData.length >= 0) && (
                    <div className="invoice-table-scroll-wrapper">
                    <div className="invoice-table">
                        {!isMobile && (
                            <TableHeader>
                                {[
                                    t('INVOICE', { defaultValue: 'Invoice' }),
                                    t('CLIENT', { defaultValue: 'Client' }),
                                    t('AMOUNT'),
                                    t('STAGE_LABEL'),
                                    t('ACTIONS', { defaultValue: 'Actions' }),
                                ].map((header, index) => (
                                    <TableColumn key={index}>{header}</TableColumn>
                                ))}
                            </TableHeader>
                        )}

                        <TableBody>
                            {loading && (
                                <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                                    {t('LOADING_INVOICES')}
                                </div>
                            )}
                            {isMobile ? (
                                // Mobile: Card-based layout
                                (Array.isArray(invoicesData) ? invoicesData : [])?.map((invoice: any) => (
                                    <MobileCardWrapper key={invoice.id}>
                                        <MobileCardRow label={t('INVOICE_NO') }>
                                            <span className="mi-invoice__number">{invoice.id}</span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('CLIENT_NAME')}>
                                            {(() => {
                                                const clientName = invoice?.customer?.name ?? '';
                                                return (
                                                    <span className="mi-client">
                                                        <span className={`mi-client__avatar mi-client__avatar--v${getAvatarVariant(clientName)}`}>{getClientInitial(clientName)}</span>
                                                        <span className="mi-client__info">
                                                            <span className="mi-client__name">{clientName}</span>
                                                            {invoice?.customer?.email && <span className="mi-client__email">{invoice.customer.email}</span>}
                                                        </span>
                                                    </span>
                                                );
                                            })()}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('AMOUNT')}>
                                            <span className="mi-amount">{formatAmount(invoice.amount)} {invoice.currency}</span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('STAGE_LABEL')}>
                                            <span className={getStageBadgeClass(invoice.invoiceStatus)}>
                                                {t('DRAFT')}
                                                {/* {invoice.invoiceStatus === 2 && <CheckIcon />} */}
                                                {/* {t(mapInvoiceStatus(invoice.invoiceStatus)) || mapInvoiceStatus(invoice.invoiceStatus)} */}
                                            </span>
                                        </MobileCardRow>

                                        <MobileCardRow label={t('ACTION_LABEL')}>
                                            <div className="invoice-table-detailed__action-group">
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    data-tip={t("EDIT_INVOICE")}
                                                    onClick={() => handleDraftInvoice(invoice.id)}
                                                >
                                                    <EditIconBtn />
                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    data-tip={t('DOWNLOAD', { defaultValue: 'Download' })}
                                                    onClick={() => handleDownloadDraftInvoice(invoice.id, 'sv-se')}
                                                >
                                                    <DownloadIcon />
                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    data-tip={t('DELETE_INVOICE')}
                                                    onClick={() => openDeleteModal(invoice.id)}

                                                    disabled={editingInvoiceNumber === invoice.invoiceNumber}
                                                >
                                                    <DeleteIcon />
                                                </button>
                                            </div>
                                        </MobileCardRow>
                                    </MobileCardWrapper>
                                ))
                            ) : (
                                (invoicesData as any[]).map((invoice: any) => (
                                    <TableRow key={invoice.id}>
                                        <TableCell>
                                            <span className="mi-invoice__number">{invoice.id}</span>
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                const clientName = invoice?.customer?.name ?? invoice.clientName ?? '';
                                                return (
                                                    <span className="mi-client">
                                                        <span className={`mi-client__avatar mi-client__avatar--v${getAvatarVariant(clientName)}`}>{getClientInitial(clientName)}</span>
                                                        <span className="mi-client__info">
                                                            <span className="mi-client__name">{clientName}</span>
                                                            {invoice?.customer?.email && <span className="mi-client__email">{invoice.customer.email}</span>}
                                                        </span>
                                                    </span>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell><span className="mi-amount">{formatAmount(invoice.amount)} {invoice.currency}</span></TableCell>
                                        <TableCell>
                                            <span className={getStageBadgeClass(invoice?.customerApproved)}>
                                                {/* {invoice.isManually
                                                    ? "N/A"
                                                    : (() => {
                                                        switch (invoice?.customerApproved) {
                                                            case null:
                                                                return t("Pending");
                                                            case true:
                                                                return t("Approved");
                                                            case false:
                                                                return t("Unapproved");
                                                            default:
                                                                return "";
                                                        }
                                                    })()
                                                } */}
                                                {t('DRAFT')}

                                            </span>

                                        </TableCell>
                                        <TableCell>
                                            <div className="invoice-table-detailed__action-group">
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    data-tip={t("EDIT_INVOICE")}
                                                    onClick={() => handleDraftInvoice(invoice.id)}
                                                >
                                                    <EditIconBtn />
                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    data-tip={t('DOWNLOAD', { defaultValue: 'Download' })}
                                                    onClick={() => handleDownloadDraftInvoice(invoice.id, 'sv-se')}
                                                >
                                                    <DownloadIcon />
                                                </button>
                                                <button
                                                    className="invoice-table-detailed__action-btn"
                                                    data-tip={t('DELETE_INVOICE')}
                                                    onClick={() => openDeleteModal(invoice.id)}

                                                    disabled={editingInvoiceNumber === invoice.invoiceNumber}
                                                >
                                                    <DeleteIcon />
                                                </button>
                                            </div>
                                            <div className="invoice-table-detailed__action-group">
                                                {/* {getInvoiceActions(invoice).map((btn, i) => (
                                                    <button
                                                        key={i}
                                                        className="invoice-table-detailed__action-btn"
                                                        title={btn.title}                                                    >
                                                        {btn.icon}
                                                    </button>
                                                ))} */}
                                            </div>

                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </div>

                    {/* Themed empty state — violet illustration + title +
                        subtitle with entrance fade-up, floating illustration,
                        and pulsing halo. Lifted OUT of <TableBody> because
                        that element is `display: table-row-group` and boxes
                        any non-row child into an anonymous cell in the first
                        column (~140px wide, left-pinned). Sitting it here as
                        a sibling of .invoice-table puts it in normal block
                        flow so it spans the full scroll wrapper width and
                        centers cleanly under the violet header bar.
                        All inline-styled so it has no dependency on the
                        salary-notice-page's .sn-empty SCSS (which is
                        scoped there). */}
                    {!loading && !error && invoicesData && Array.isArray(invoicesData) && invoicesData.length === 0 && (
                        <div
                            role="status"
                            aria-live="polite"
                            style={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '64px 24px',
                                textAlign: 'center',
                                gap: 14,
                                animation: 'draft-empty-enter 0.45s ease-out both',
                            }}
                        >
                            <div
                                aria-hidden="true"
                                style={{
                                    position: 'relative',
                                    width: 132,
                                    height: 132,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {/* Soft pulsing halo behind the illustration */}
                                <span
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        borderRadius: '50%',
                                        background: 'radial-gradient(closest-side, rgba(168, 85, 247, 0.22), rgba(168, 85, 247, 0))',
                                        animation: 'draft-empty-halo 3.2s ease-in-out infinite',
                                    }}
                                />
                                <svg width="96" height="96" viewBox="0 0 64 64" fill="none" style={{ position: 'relative', zIndex: 1, animation: 'draft-empty-float 4s ease-in-out infinite' }}>
                                    <defs>
                                        <linearGradient id="draft-empty-grad" x1="10" y1="12" x2="54" y2="22" gradientUnits="userSpaceOnUse">
                                            <stop stopColor="#a855f7" />
                                            <stop offset="1" stopColor="#7c3aed" />
                                        </linearGradient>
                                    </defs>
                                    {/* Invoice card (draft = single sheet with a dog-eared corner) */}
                                    <path d="M14 10h26l10 10v34a2 2 0 0 1-2 2H14a2 2 0 0 1-2-2V12a2 2 0 0 1 2-2z" fill="#fff" stroke="#7c3aed" strokeOpacity="0.25" strokeWidth="2" />
                                    <path d="M40 10v10h10" fill="#fff" stroke="#7c3aed" strokeOpacity="0.25" strokeWidth="2" strokeLinejoin="round" />
                                    {/* Violet-gradient header strip */}
                                    <rect x="12" y="22" width="38" height="6" fill="url(#draft-empty-grad)" />
                                    {/* Dashed placeholder rows */}
                                    <line x1="18" y1="34" x2="46" y2="34" stroke="#7c3aed" strokeOpacity="0.20" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="2 4" />
                                    <line x1="18" y1="40" x2="42" y2="40" stroke="#7c3aed" strokeOpacity="0.18" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="2 4" />
                                    <line x1="18" y1="46" x2="44" y2="46" stroke="#7c3aed" strokeOpacity="0.14" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="2 4" />
                                    {/* $ badge signalling "invoice" */}
                                    <circle cx="42" cy="52" r="6" fill="url(#draft-empty-grad)" />
                                    <text x="42" y="55.5" fill="#fff" fontSize="8" fontWeight="700" fontFamily="Plus Jakarta Sans, sans-serif" textAnchor="middle">$</text>
                                </svg>
                            </div>
                            <p style={{ margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 16, color: '#1e1b4b' }}>
                                {t('NO_INVOICES_YET', { defaultValue: 'No invoices yet' })}
                            </p>
                            <p style={{ margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500, fontSize: 13.5, color: '#5b21b6', opacity: 0.78, maxWidth: 380 }}>
                                {t('NO_INVOICES_HINT', { defaultValue: 'Your invoices will show up here once you create them.' })}
                            </p>
                            {/* Inline @keyframes — scoped via the <style> tag,
                                mounted only while the empty state is visible. */}
                            <style>{`
                                @keyframes draft-empty-halo {
                                    0%, 100% { transform: scale(1); opacity: 0.55; }
                                    50%      { transform: scale(1.08); opacity: 0.85; }
                                }
                                @keyframes draft-empty-enter {
                                    from { opacity: 0; transform: translateY(8px); }
                                    to   { opacity: 1; transform: translateY(0); }
                                }
                                @keyframes draft-empty-float {
                                    0%, 100% { transform: translateY(0); }
                                    50%      { transform: translateY(-4px); }
                                }
                            `}</style>
                        </div>
                    )}
                    </div>
                )}

                {/* Pagination */}
                {pagination.totalPages > 0 && (
                    <div className="invoice-table-footer">
                        <span className="invoice-table-footer__count">
                            {(() => {
                                const total = (pagination as any).totalCount ?? 0;
                                const size = (pagination as any).pageSize ?? 10;
                                const start = total === 0 ? 0 : (pagination.currentPage - 1) * size + 1;
                                const end = Math.min(pagination.currentPage * size, total);
                                return t('SHOWING_INVOICES_COUNT', {
                                    start,
                                    end,
                                    total,
                                    defaultValue: 'Showing {{start}}–{{end}} of {{total}} invoices',
                                });
                            })()}
                        </span>
                        <div className="invoice-table-pagination">
                        <div className="pagination-controls">
                            <button
                                className="pagination-button pagination-button--prev"
                                onClick={() => goToPreviousPage()}
                                disabled={pagination.currentPage === 1}
                            >
                                <ArrowLeftIcon />
                                <span>{t('PREV_STEP')}</span>
                            </button>

                            {calculatePaginationPages(pagination.currentPage, pagination.totalPages).map((page, index) => {
                                if (page === '...') {
                                    return (
                                        <button
                                            key={`ellipsis-${index}`}
                                            className="pagination-button pagination-button--number"
                                            disabled
                                        >
                                            ...
                                        </button>
                                    );
                                }
                                return (
                                    <button
                                        key={page}
                                        className={`pagination-button pagination-button--number${page === pagination.currentPage ? ' pagination-button--active' : ''
                                            }`}
                                        onClick={() => setCurrentPage(page as number)}
                                    >
                                        {page}
                                    </button>
                                );
                            })}

                            <button
                                className="pagination-button pagination-button--next"
                                onClick={() => goToNextPage()}
                                disabled={pagination.currentPage === pagination.totalPages}
                            >
                                <span>{t('NEXT_STEP') || 'Next'}</span>
                                <ArrowRightIcon />
                            </button>
                        </div>
                        </div>
                    </div>
                )}
            </div>
            <ConfirmDeleteModal
    open={showDeleteModal}
    title={t('DELETE_INVOICE')}
    description={t('DELETE_DRAFT_CONFIRM')}
    loading={deleteLoading}
    onCancel={closeDeleteModal}
    onConfirm={confirmDelete}
/>

        </div>
    );
};


